// E-Ticaret › Kampanyalar (Kampanya Yönetimi + Kargo) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'ecommerce.campaigns';

export const ecommerceCampaignsGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Kampanyalar nasıl yönetilir?'),
  intro: t(`${G}.intro`, 'Web sitenizdeki ürün indirimi, kupon, sepet indirimi, X al Y öde ve ücretsiz kargo kampanyalarını buradan açarsınız. Kurallar yanlış girilirse sitedeki fiyatlar hatalı çıkar; kaydetmeden önce "Sepeti dene" ile kontrol edin. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Kampanyalar / Kargo sekmeleri'),
    toolbar: t(`${G}.regions.toolbar`, 'Sepeti dene ve Yeni Kampanya'),
    filters: t(`${G}.regions.filters`, 'Durum sekmeleri, arama, özel gün'),
    table: t(`${G}.regions.table`, 'Kampanya listesi'),
  },
  steps: [
    {
      region: 'toolbar',
      title: t(`${G}.s1.title`, '1. Kampanya türünü seçin'),
      text: t(`${G}.s1.text`, '"Yeni Kampanya" dört adımlı bir sihirbaz açar: Tür, Bilgiler, Kurallar, Kapsam. İlk adımda bir ana tür seçin. Tür kayıttan sonra değiştirilemez.'),
      tips: [
        t(`${G}.s1.tip1`, 'Ürün İndirimi: seçili ürün / kategori / markada yüzde veya tutar indirimi; ürün kartı ve detay fiyatına işler.'),
        t(`${G}.s1.tip2`, 'Kupon Kodu: müşteri sepette kod girince indirim. Sepet İndirimi: kod istemez, sepet tutarı eşiği dolunca otomatik düşer.'),
        t(`${G}.s1.tip3`, 'X Al Y Öde: kapsamdaki adede göre bedava birimleri düşer. Ücretsiz Kargo: eşik dolunca kargo sıfırlanır; fiyat indirimleriyle yarışmaz.'),
        t(`${G}.s1.tip4`, 'Ana türün yanına yalnızca Ücretsiz Kargo eklenebilir. Kupon, sepet indirimi ve X al Y öde aynı kayıtta birleşmez; bunları ayrı kampanya olarak açın.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s2.title`, '2. Bilgileri ve tarihleri girin'),
      text: t(`${G}.s2.text`, '"Bilgiler" adımında Kampanya Adı (zorunlu), Açıklama, Özel Gün / Sezon, Başlangıç ve Bitiş tarihlerini girin; bitiş başlangıçtan önce olamaz. "Kampanya aktif" kapalıysa kampanya tarihleri içinde de uygulanmaz.'),
    },
    {
      region: 'toolbar',
      title: t(`${G}.s3.title`, '3. Kuralları ve tutarı belirleyin'),
      text: t(`${G}.s3.text`, '"Kurallar" adımında seçtiğiniz türler alt alta listelenir. Bu tek bir kampanya kaydıdır: İndirim Türü (Yüzde / Tutar), İndirim Değeri, isteğe bağlı Maks. İndirim Tutarı ve Min. Sepet Tutarı türler arasında paylaşılır. Ayrı bir kupon kampanyasını ise müşteri sepette kendisi ekler.'),
      tips: [
        t(`${G}.s3.tip1`, 'Kupon kodu 6 haneli olmalı, yalnız harf ve rakam içerebilir. Kod ilk kullanımdan sonra değiştirilemez.'),
        t(`${G}.s3.tip2`, 'Toplam Limit kuponun toplam kaç kez kullanılabileceğini, Kişi Başı Limit bir müşterinin kaç kez kullanabileceğini belirler. Kişi başı limit üye hesabına, misafirde sipariş e-postasına yazılır; ödeme başarılı olunca hak düşer.'),
        t(`${G}.s3.tip3`, 'X Al Y Öde: Al (X) ve Öde (Y) adetlerini girin. "Bedava Birim" En ucuz bedava (müşteri lehine) veya En pahalı bedava (mağaza lehine) olabilir; "En Fazla Bedava Adet" boşsa sınırsızdır.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s4.title`, '4. İndirimler nasıl birleşir?'),
      text: t(`${G}.s4.text`, 'Otomatik kampanyalarda (ürün indirimi / sepet indirimi / X al Y öde) indirimler üst üste binmez; müşteriye daha çok indirim sağlayan kazanır, sıra elle girilmez. Ücretsiz kargo bunlardan bağımsız çalışır.'),
      tips: [
        t(`${G}.s4.tip1`, 'Kupon kartındaki "Diğer indirimlerle birleştir" açıksa kupon kazanan otomatik indirimin üzerine biner (ör. 120 → 96 → ~81,6); her siparişte çift kesinti olur.'),
        t(`${G}.s4.tip2`, 'Kapalıysa (varsayılan, kârı korur) müşteri kupon yazınca yalnız kupon uygulanır; ürün / sepet indirimi o siparişte düşer.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s5.title`, '5. Kapsamı seçin ve kaydedin'),
      text: t(`${G}.s5.text`, '"Kapsam" adımında kampanyanın Tüm Ürünler, Seçili Ürünler, Seçili Kategoriler veya Seçili Markalar için geçerli olacağını seçin; seçili kapsamda en az bir ürün / kategori / marka seçmelisiniz. Alttaki Özet\'i kontrol edip "Kaydet"e basın.'),
      tips: [
        t(`${G}.s5.tip1`, 'Kayıttan sonra ana sayfada küçük bir kampanya kartı oluşur ve Promosyon Kartları (Kart Oluştur) bölümünde otomatik tanımlanır. İsterseniz kartı kapatabilir, başka sayfaya veya konuma taşıyabilirsiniz. Kampanyanın vitrin adresi /campaigns/<kampanya no> olur.'),
        t(`${G}.s5.tip2`, 'Düzenlerken ad, tarih, tutar ve kapsam değiştirilebilir; tür ve kullanılmış kupon kodu kilitli kalır.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s6.title`, '6. Listeyi izleyin'),
      text: t(`${G}.s6.text`, 'Tümü / Aktif / Yaklaşan / Süresi Doldu / Pasif sekmeleri kampanyaları durumuna göre ayırır. Kampanya adı veya kupon kodu ile arayın, Özel Gün / Sezon ile süzün. Satırdaki "Aktif" anahtarı kampanyayı hemen açar / kapatır; kapatınca ana sayfadaki kampanya kartı da kapanır.'),
      tips: [
        t(`${G}.s6.tip1`, '"Kullananları gör" kampanyanın hangi siparişte, hangi müşteri tarafından ve ne kadar indirimle kullanıldığını gösterir; iade / iptal olan kullanımlar ayrıca işaretlenir.'),
        t(`${G}.s6.tip2`, 'Süresi biten kampanyayı "Tekrarla" ile aynı kurallarla yeni tarihe kopyalayın; kuponlu kampanyada yeni kod üretilir.'),
        t(`${G}.s6.tip3`, '"Sepeti dene" penceresinde ürün ve isterseniz kupon kodu ekleyip "Hesapla"ya basın: kayıtlı kampanyalardan hangisinin kazanacağını ve ödenecek tutarı görürsünüz. Sipariş veya kullanım kaydı oluşmaz.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s7.title`, '7. Kargo ücretini ve ücretsiz kargo eşiğini ayarlayın'),
      text: t(`${G}.s7.text`, '"Kargo" sekmesinde kargo bedelini ve ücretsiz kargo eşiğini girersiniz; değişiklikler otomatik kaydedilir. Sepetteki görünen ürün tutarı eşiğe ulaşınca kargo ücretsiz olur. Kargo sistemi kapalıyken sepette kargo her zaman ücretsiz görünür.'),
      tips: [
        t(`${G}.s7.tip1`, 'Ayrıca bir Ücretsiz Kargo kampanyası açıksa müşteri iki eşikten hangisine önce ulaşırsa kargo bedava olur. Tek eşik istiyorsanız ya bu eşiği kullanın ya da kampanyadaki ücretsiz kargoyu kapatın.'),
        t(`${G}.s7.tip2`, 'Başlıktaki (i) bu rehberi yeniden açar.'),
      ],
    },
  ],
});
